import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { ViewValidators } from 'src/app/core/validators/view.validators';
import { ViewEdit } from '../model/viewEdit';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { ReportService } from 'src/app/core/service/report.service';
import { sortBy } from 'lodash';
import { UserService } from 'src/app/core/service/user.service';
import { ViewService } from 'src/app/core/service/view.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { merge } from 'lodash';

@Component({
  standalone: false,
  selector: 'app-view-edit',
  templateUrl: './view-edit.component.html',
  styleUrls: ['./view-edit.component.scss']
})
export class ViewEditComponent
{
  @ViewChild('activeUserRoles') public activeUserRoles: MultiSelectComponent;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<ViewEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public viewsLookup: any = [];
  public views: Array<string> = [];
  public viewNames: Array<{ text: string; value: string }> = [];
  public activeRoles: Array<{ text: string; value: string }>;
  public disableControls = false;

  private viewEdit: ViewEdit;

  constructor(
    private fb: UntypedFormBuilder,
    private viewValidators: ViewValidators,
    private reportService: ReportService,
    private userService: UserService,
    private viewService: ViewService,
    private messageHandlerService: MessageHandlerService,
  )
  {
    this.activeRoles = sortBy(this.userService.roles.filter(r => r.value !== 'ROLE_COMMUNITY_USER' && r.value !== 'ROLE_DIRECTORY_MANAGER' && r.value !== 'ROLE_COMMUNITY_ADMIN' &&
      r.value !== 'ROLE_REVENUE_MANAGER_OPERATOR' && r.value !== 'ROLE_REVENUE_MANAGER_ANALYST' && r.value !== 'ROLE_CBO_REVENUE_ANALYST' &&
      r.value !== 'ROLE_SYSTEM_OPERATOR'), ['text']);
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name'); // display name
  }

  public get viewName(): UntypedFormControl
  {
    return this.editForm.get('viewName');
  }

  public get viewType(): UntypedFormControl
  {
    return this.editForm.get('viewType');
  }

  public get authorizedRoles(): UntypedFormControl
  {
    return this.editForm.get('authorizedRoles');
  }


  @Input() public set model(view: ViewEdit)
  {
    this.initModel(view);
  }

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = merge(this.viewEdit, this.editForm.value);
    objectToPersist.id = (this.isNew) ? 0 : objectToPersist.id;
    objectToPersist.authorizedRoles = this.editForm.controls.authorizedRoles.value;

    this.viewService.viewByViewNameExists(objectToPersist.viewName, objectToPersist.id).subscribe({
      next: (response) =>
      {
        if (response)
        {
          this.messageHandlerService.displayWarning('VIEW-ALREADY-DEFINED');
          return;
        }
        else
        {
          this.save.emit(objectToPersist);
          this.editFormActive = false;
        }
      }
    });
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(viewEdit: ViewEdit)
  {
    if (viewEdit === undefined)
    {
      return;
    }

    // Set the current view id used for name async validators
    const previousViewName = this.isNew ? '' : viewEdit.name;

    // Set form
    this.editForm = this.fb.group({
      id: [viewEdit.id],
      version: [viewEdit.version],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.viewValidators.validateViewNameUnique(this.editForm.get('name'), previousViewName)
        }
      ],
      viewType: [
        { value: viewEdit.viewType, disabled: false }, Validators.required
      ],
      viewName: [
        {
          value: viewEdit.viewName,
          disabled: (viewEdit.viewType !== 'METRIC' && viewEdit.viewType !== 'REPORT')
        }, Validators.required
      ],
      authorizedRoles: [
        { value: viewEdit.authorizedRoles, disabled: false }, Validators.required
      ],
    });

    this.editForm.get('viewType').valueChanges.subscribe({
      next: () =>
      {
        this.setViewsAndState();
      }
    });

    this.editForm.reset(viewEdit);
    this.viewEdit = viewEdit;
    this.editFormActive = viewEdit !== undefined;

    if (!this.isNew)
    {
      // Check if in use
      this.viewService.viewUsedInExtract(viewEdit.viewName).subscribe({
        next: (inUseResponse) =>
        {
          if (inUseResponse)
          {
            this.viewType.disable();
            this.viewName.disable();
            this.disableControls = true;
          }
          else
          {
            this.viewType.enable();
            this.viewName.enable();
            this.disableControls = false;
          }
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  private setViewsAndState(): void
  {
    if (this.viewType.value === 'METRIC' || this.viewType.value === 'REPORT')
    {
      this.viewName.enable();
      this.reportService.getReportViews(this.viewType.value).subscribe({
        next: (views) =>
        {
          this.views = views.sort();
          const viewNames = [];
          this.views.forEach(data =>
          {
            viewNames.push({ text: data, value: data });
            this.viewNames = sortBy(viewNames, ['text']);
          });

          // Reset view name if no longer available
          if (!this.views.includes(this.viewName.value))
          {
            this.viewName.setValue(null);
          }
        }
      });
    }
  }

}
